// src/pages/login/LoginPage.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { alpha, styled } from '@mui/material/styles';
import {
    Alert,
    Box,
    Button,
    Card as MuiCard,
    CssBaseline,
    Divider,
    FormControl,
    FormLabel,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import { INACTIVITY_LOGOUT_REASON } from '../../auth/useInactivityLogout';
import { login, loginWithWindowsPassword, MANUAL_LOGOUT_KEY } from '../../services/auth/authService';
import { getLoginErrorMessage } from '../../services/requestUtils';
import { getLoginTenantSettings } from '../../services/tenantSettings/tenantSettingsService';
import { BRAND_COLORS } from '../../theme/brandColors';
import WindowsLoginModal from './WindowsLoginModal';

const loginLogoUrl = `${process.env.PUBLIC_URL}/White_Background.svg`;

const SignInContainer = styled(Stack)(({ theme }) => ({
    position: 'relative',
    minHeight: '100dvh',
    padding: theme.spacing(2),
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: BRAND_COLORS.black,
    backgroundImage: `linear-gradient(145deg, ${BRAND_COLORS.red} 0%, ${BRAND_COLORS.redDark} 42%, ${BRAND_COLORS.black} 100%)`,
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(4),
    },
}));

const Card = styled(MuiCard)(({ theme }) => ({
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    maxWidth: 450,
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(BRAND_COLORS.black, 0.08)}`,
    boxShadow: `0 8px 32px ${alpha(BRAND_COLORS.black, 0.08)}`,
}));

export default function LoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [windowsOpen, setWindowsOpen] = useState(false);
    const [windowsError, setWindowsError] = useState('');
    const [windowsLoading, setWindowsLoading] = useState(false);

    const isManualLogout = localStorage.getItem(MANUAL_LOGOUT_KEY) === '1';
    const isInactivityLogout = location.state?.reason === INACTIVITY_LOGOUT_REASON;
    const isSessionExpired = new URLSearchParams(location.search).get('reason') === 'expired';

    const { data: loginConfig } = useQuery({
        queryKey: ['tenantLoginConfig'],
        queryFn: ({ signal }) => getLoginTenantSettings(signal),
        staleTime: 5 * 60 * 1000,
        retry: false,
    });
    const showWindowsLogin = loginConfig?.useWindowsAuth === true;
    const defaultDomain = loginConfig?.defaultDomain || '';

    const statusMessage = (() => {
        if (isInactivityLogout) {
            return 'You were logged out due to inactivity. Please sign in again.';
        }
        if (isSessionExpired) {
            return 'Your session has expired. Please sign in again.';
        }
        if (isManualLogout) {
            return 'You have logged out. Please sign in again.';
        }
        return null;
    })();

    const hidePassword = () => setShowPassword(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await login(username, password);
            navigate('/');
        } catch (err) {
            setError(getLoginErrorMessage(err, 'Invalid username or password'));
        } finally {
            setLoading(false);
        }
    };

    const openWindowsModal = () => {
        setError('');
        setWindowsError('');
        setWindowsOpen(true);
    };

    const handleWindowsSubmit = async ({ domain, username: winUser, password: winPass }) => {
        setWindowsError('');
        setWindowsLoading(true);
        try {
            await loginWithWindowsPassword({ domain, username: winUser, password: winPass });
            setWindowsOpen(false);
            navigate('/');
        } catch (err) {
            setWindowsError(
                getLoginErrorMessage(err, 'Windows login failed. Check domain, username, and password.')
            );
        } finally {
            setWindowsLoading(false);
        }
    };

    return (
        <>
            <CssBaseline />
            <SignInContainer direction="column">
                <Card variant="outlined">
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                        <Box
                            component="img"
                            src={loginLogoUrl}
                            alt="Precia Molen"
                            sx={{ width: '50%', height: 'auto', maxWidth: 280 }}
                        />
                    </Box>

                    <Typography
                        component="h1"
                        variant="h4"
                        sx={{
                            fontWeight: 600,
                            textAlign: 'center',
                            color: (theme) =>
                                theme.palette.mode === 'dark' ? '#fff' : BRAND_COLORS.black,
                        }}
                    >
                        Sign in
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: 'center', mt: -1 }}
                    >
                        Precia Molen GenWeb
                    </Typography>

                    {statusMessage && (
                        <Alert severity="info" sx={{ mt: 0.5 }}>
                            {statusMessage}
                        </Alert>
                    )}

                    <Box
                        component="form"
                        onSubmit={handleLogin}
                        noValidate
                        sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
                    >
                        <FormControl>
                            <FormLabel htmlFor="username">Username</FormLabel>
                            <TextField
                                id="username"
                                name="username"
                                placeholder="Your Username"
                                autoComplete="username"
                                autoFocus
                                required
                                fullWidth
                                size="medium"
                                variant="outlined"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                disabled={loading || windowsLoading}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel htmlFor="password">Password</FormLabel>
                            <TextField
                                id="password"
                                name="password"
                                placeholder="Your password"
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="current-password"
                                required
                                fullWidth
                                size="medium"
                                variant="outlined"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading || windowsLoading}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="Hold to show password"
                                                    edge="end"
                                                    disabled={loading || windowsLoading}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        setShowPassword(true);
                                                    }}
                                                    onMouseUp={hidePassword}
                                                    onMouseLeave={hidePassword}
                                                    onTouchStart={(e) => {
                                                        e.preventDefault();
                                                        setShowPassword(true);
                                                    }}
                                                    onTouchEnd={hidePassword}
                                                >
                                                    <VisibilityOutlinedIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                        </FormControl>

                        {error && (
                            <Alert severity="error">{error}</Alert>
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading || windowsLoading}
                            sx={{ mt: 0.5 }}
                        >
                            Sign in
                        </Button>
                    </Box>

                    {showWindowsLogin && (
                        <>
                            <Divider sx={{ color: BRAND_COLORS.gray }}>or</Divider>

                            <Button
                                fullWidth
                                variant="outlined"
                                color="inherit"
                                onClick={openWindowsModal}
                                disabled={loading || windowsLoading}
                                startIcon={<MicrosoftIcon />}
                                sx={{
                                    borderColor: (theme) =>
                                        theme.palette.mode === 'dark'
                                            ? alpha(BRAND_COLORS.white, 0.5)
                                            : alpha(BRAND_COLORS.black, 0.23),
                                    color: (theme) =>
                                        theme.palette.mode === 'dark'
                                            ? BRAND_COLORS.white
                                            : BRAND_COLORS.black,
                                    '&:hover': {
                                        borderColor: (theme) =>
                                            theme.palette.mode === 'dark'
                                                ? BRAND_COLORS.white
                                                : BRAND_COLORS.black,
                                        backgroundColor: (theme) =>
                                            theme.palette.mode === 'dark'
                                                ? alpha(BRAND_COLORS.white, 0.08)
                                                : alpha(BRAND_COLORS.black, 0.04),
                                    },
                                }}
                            >
                                Sign in with Windows
                            </Button>
                        </>
                    )}
                </Card>
            </SignInContainer>

            <WindowsLoginModal
                open={windowsOpen}
                onClose={() => !windowsLoading && setWindowsOpen(false)}
                onSubmit={handleWindowsSubmit}
                defaultDomain={defaultDomain}
                loading={windowsLoading}
                error={windowsError}
            />
        </>
    );
}
